import { getStickerPanel, addSticker, deleteSticker, pinStickerFront } from '@/api/sticker'
import officialPacks from '@/assets/sticker/packs.json'

/**
 * 每个用户最多能收藏多少张表情。
 * 与后端 StickerUrlUtils.MAX_USER_STICKER_COUNT 一致，改一处要改两处。
 * 这里留一份是为了在本地就拦住必然失败的自动收藏请求，见 collectStickerAfterSend。
 */
const STICKER_MAX_COUNT = 100

function normalizeList(list) {
  if (!Array.isArray(list)) return []
  return list.filter(item => item && item.url).map(item => ({
    id: item.id || '',
    url: item.url,
    name: item.name || '',
    added: !!item.added
  }))
}

function flattenPacks(packs) {
  const list = []
  ;(packs || []).forEach(pack => {
    ;(pack.stickers || []).forEach(item => list.push(item))
  })
  return list
}

export default {
  data() {
    return {
      stickerMine: [],
      stickerPacks: [],
      stickerLoading: false,
      stickerLoaded: false,
      stickerToolbarPack: 'mine',
      // 收藏已满，别再为每条发出去的表情白打一次请求。见 collectStickerAfterSend
      stickerQuotaReached: false
    }
  },
  created() {
    // 先把内置包摆出来，面板在网络返回前就不是空的
    this.stickerPacks = this.mergePacks([])
  },
  methods: {
    onStickerPickerOpen() {
      if (!this._keepStickerPack) this.stickerToolbarPack = 'mine'
      this._keepStickerPack = false
      this.loadStickerPanel()
    },
    /**
     * 点工具栏上某个表情包的封面：切到该包并把面板顶出来。
     *
     * 调用处必须写成 `@click.stop="openStickerPackFromToolbar(pack.id, $event)"`。
     *
     * 为什么 .stop 是必需的：封面 span 是 el-popover 的兄弟节点，而 popover 在
     * trigger="click" 下会在 document 上挂 handleDocumentClick —— 只要点击点不在
     * reference / popper 内就把 showPopper 置回 false。偏偏 Vue 的 nextTick 是微任务，
     * 在事件冒泡到 document 之前就已经把 visible=true 刷进了 showPopper，于是一次点击
     * 里先开后关，用户看到的就是「点了没反应」（原来私聊和评论区的表情包封面全是哑的，
     * 就是这个原因；点「斗图」入口能开，只因为那个按钮本身就是 popover 的 reference，
     * handleDocumentClick 会提前 return）。
     *
     * 第二个参数是兜底：万一调用处漏了 .stop，这里再挡一次，行为保持一致。
     */
    openStickerPackFromToolbar(id, e) {
      if (e && e.stopPropagation) e.stopPropagation()

      const picker = this.$refs.stickerPicker
      const pack = id || 'mine'
      const opened = !!(picker && picker.isOpen && picker.isOpen())

      // 再点一次当前分组的封面 = 收起面板，和点「斗图」入口的开合手感一致
      if (opened && this.stickerToolbarPack === pack) {
        if (picker.close) picker.close()
        return
      }

      this.stickerToolbarPack = pack

      if (!opened) {
        // 只有真的要触发 @show 时才置这个标记。面板已开着的情况下 @show 不会再来，
        // 标记留在 true 会让下一次点「斗图」入口也不回到「我添加的」。
        this._keepStickerPack = true
        if (picker && picker.open) picker.open()
        return
      }
      // 面板本来就开着，只是换了分组：不会触发 @show，自己补一次拉取和重定位
      this.loadStickerPanel()
      if (picker.refresh) picker.refresh()
    },
    markStickerAdded(url, added) {
      this.stickerPacks = this.stickerPacks.map(pack => ({
        ...pack,
        stickers: (pack.stickers || []).map(s => (
          s.url === url ? { ...s, added } : s
        ))
      }))
    },
    mergePacks(remote) {
      const mineUrls = new Set(this.stickerMine.map(item => item.url))
      const remoteList = normalizeList(remote)
      const packs = (officialPacks || []).map(pack => ({
        id: pack.id,
        name: pack.name,
        cover: pack.cover,
        stickers: normalizeList(pack.stickers).map(item => ({
          ...item,
          added: mineUrls.has(item.url)
        }))
      }))
      const known = new Set(flattenPacks(packs).map(item => item.url))
      const extra = remoteList
        .filter(item => !known.has(item.url))
        .map(item => ({
          ...item,
          added: item.added || mineUrls.has(item.url)
        }))
      if (extra.length) {
        packs.push({
          id: 'more',
          name: '更多',
          cover: extra[0].url,
          stickers: extra
        })
      }
      return packs
    },

    /**
     * 拉取表情面板。
     *
     * stickerLoaded 只在**成功**时置位：原来不论成功失败都置 true，于是首次打开面板时
     * 网络抖一下，「我添加的」就永远是空的、推荐区只剩本地内置包，直到页面被重建
     * ——  而 force 参数没有任何调用方会传 true，等于没有退路。失败时保持 false，
     * 下次打开面板自然重试；stickerLoading 仍然挡住并发请求。
     *
     * 两种失败都要可重试：抛异常（断网、超时）和 code !== 200（未登录、服务端错误）。
     */
    async loadStickerPanel(force) {
      if (this.stickerLoading) return
      if (this.stickerLoaded && !force) return
      this.stickerLoading = true
      try {
        const res = await getStickerPanel()
        if (res && res.code === 200 && res.data) {
          this.stickerMine = normalizeList(res.data.mine)
          this.stickerPacks = this.mergePacks(res.data.recommend)
          this.stickerQuotaReached = this.stickerMine.length >= STICKER_MAX_COUNT
          this.stickerLoaded = true
        } else {
          // 没拿到数据时至少把内置包摆出来，但不标记已加载
          this.stickerPacks = this.mergePacks([])
        }
      } catch (e) {
        this.stickerPacks = this.mergePacks([])
      } finally {
        this.stickerLoading = false
      }
    },

    /**
     * 发送表情后顺手把它收进「我添加的」，失败不打扰用户。
     *
     * 加这一层是因为原来直接 `addStickerByUrl(url).catch(() => {})`：收藏满 100 之后
     * 服务端每次都抛「最多添加100个表情」，被静默吞掉，而 item.added 永远不会变 true
     * —— 于是此后**每发一条表情都要白打一次 POST**，而 /v1/sticker 的限流是
     * 30 次/60 秒，刷几下就开始吃 429（同样被吞掉）。
     *
     * 两道闸：本地张数先拦一次（不依赖错误文案），失败过的地址记下来不再重试。
     */
    collectStickerAfterSend(item) {
      const url = item && item.url
      if (!url) return
      if (this.stickerQuotaReached) return
      if (this.stickerMine.length >= STICKER_MAX_COUNT) {
        this.stickerQuotaReached = true
        return
      }
      if (!this._stickerCollectFailed) this._stickerCollectFailed = new Set()
      if (this._stickerCollectFailed.has(url)) return

      this.addStickerByUrl(url, item.name).catch((e) => {
        this._stickerCollectFailed.add(url)
        // 兜住"本地计数还没追上服务端"的情况，比如多端同时收藏
        if (((e && e.message) || '').indexOf('最多添加') > -1) {
          this.stickerQuotaReached = true
        }
      })
    },
    async addStickerByUrl(url, name) {
      if (!url) return null
      const res = await addSticker({ url, name: name || undefined })
      if (!res || res.code !== 200) {
        throw new Error((res && res.message) || '添加失败')
      }
      const item = res.data || { url, name }
      const exists = this.stickerMine.some(s => s.url === url || (item.id && s.id === item.id))
      if (!exists) {
        this.stickerMine = [{
          id: item.id || '',
          url: item.url || url,
          name: item.name || name || '',
          added: true
        }, ...this.stickerMine]
      }
      this.markStickerAdded(url, true)
      return item
    },
    moveMineToFront(id) {
      const idx = this.stickerMine.findIndex(s => String(s.id) === String(id))
      if (idx <= 0) return
      const next = this.stickerMine.slice()
      const [item] = next.splice(idx, 1)
      this.stickerMine = [item, ...next]
    },
    async pinStickerById(id) {
      if (!id || String(id).startsWith('official-')) {
        this.moveMineToFront(id)
        return
      }
      const res = await pinStickerFront(id)
      if (!res || res.code !== 200) {
        throw new Error((res && res.message) || '操作失败')
      }
      this.moveMineToFront(id)
    },
    async removeStickerById(id, url) {
      if (id && !String(id).startsWith('official-')) {
        const res = await deleteSticker(id)
        if (!res || res.code !== 200) {
          throw new Error((res && res.message) || '删除失败')
        }
      }
      this.stickerMine = this.stickerMine.filter(s => s.id !== id && s.url !== url)
      this.markStickerAdded(url, false)
      // 删掉一张就又有位置了，重新放开自动收藏
      if (this.stickerMine.length < STICKER_MAX_COUNT) {
        this.stickerQuotaReached = false
        if (this._stickerCollectFailed) this._stickerCollectFailed.clear()
      }
    }
  }
}
